import api from './api';

/**
 * Servicio para gestionar las etapas de tratamiento
 */
const etapaTratamientoService = {
  /**
   * Obtener todas las etapas con filtrado opcional
   * @param {Object} filters - Filtros para la consulta
   * @returns {Promise} - Promesa con los resultados
   */
  getAll: async (filters = {}) => {
    const { 
      especialidad_id, 
      programa_id, 
      search, 
      page = 1, 
      limit = 10 
    } = filters;
    
    let url = '/etapas?';
    
    if (especialidad_id) url += `especialidad_id=${especialidad_id}&`;
    if (programa_id) url += `programa_id=${programa_id}&`;
    if (search) url += `search=${encodeURIComponent(search)}&`;
    url += `page=${page}&limit=${limit}`;
    
    return await api.get(url);
  },
  
  /**
   * Obtener una etapa por su ID
   * @param {number} id - ID de la etapa
   * @returns {Promise} - Promesa con los resultados
   */
  getById: async (id) => {
    return await api.get(`/etapas/${id}`);
  },
  
  /**
   * Crear una nueva etapa
   * @param {Object} data - Datos de la etapa
   * @returns {Promise} - Promesa con los resultados
   */
  create: async (data) => {
    return await api.post('/etapas', data);
  },
  
  /**
   * Actualizar una etapa existente
   * @param {number} id - ID de la etapa
   * @param {Object} data - Datos actualizados
   * @returns {Promise} - Promesa con los resultados
   */
  update: async (id, data) => {
    return await api.put(`/etapas/${id}`, data);
  },
  
  /**
   * Eliminar una etapa
   * @param {number} id - ID de la etapa
   * @returns {Promise} - Promesa con los resultados
   */
  delete: async (id) => {
    return await api.delete(`/etapas/${id}`);
  },
  
  /**
   * Obtener etapas por programa
   * @param {number} programaId - ID del programa
   * @param {number} especialidadId - ID de la especialidad (opcional)
   * @returns {Promise} - Promesa con los resultados
   */
  getByPrograma: async (programaId, especialidadId = null) => {
    let url = `/etapas/programa/${programaId}`;
    if (especialidadId) url += `?especialidad_id=${especialidadId}`;
    return await api.get(url);
  },
  
  /**
   * Obtener etapas por especialidad
   * @param {number} especialidadId - ID de la especialidad
   * @param {number} programaId - ID del programa (opcional)
   * @returns {Promise} - Promesa con los resultados
   */
  getByEspecialidad: async (especialidadId, programaId = null, historiaEspecialidadId = null) => {
    let url = `/etapas/especialidad/${especialidadId}`;
    const params = [];
    
    if (programaId) params.push(`programa_id=${programaId}`);
    if (historiaEspecialidadId) params.push(`historia_especialidad_id=${historiaEspecialidadId}`);
    
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    
    return await api.get(url);
  },
  
  /**
   * Obtener requisitos de una etapa
   * @param {number} etapaId - ID de la etapa
   * @returns {Promise} - Promesa con los resultados
   */
  obtenerRequerimientosEtapa: async (etapaId) => {
    return await api.get(`/etapas/requisitos/${etapaId}`);
  },
  
  /**
   * Obtener estadísticas de etapas
   * @param {Object} params - Parámetros para la consulta
   * @returns {Promise} - Promesa con los resultados
   */
  obtenerEstadisticasEtapas: async (programaId = null, especialidadId = null, fechaInicio = null, fechaFin = null) => {
    let url = '/etapas/estadisticas?';
    
    if (programaId) url += `programa_id=${programaId}&`;
    if (especialidadId) url += `especialidad_id=${especialidadId}&`;
    if (fechaInicio) url += `fecha_inicio=${fechaInicio.toISOString()}&`;
    if (fechaFin) url += `fecha_fin=${fechaFin.toISOString()}&`;
    
    return await api.get(url);
  },
  
  /**
   * Realizar un cambio de etapa
   * @param {number} historiaId - ID de la historia clínica
   * @param {number} etapaId - ID de la nueva etapa
   * @param {string} motivo - Motivo del cambio
   * @param {number} notificacionId - ID de la plantilla de notificación (opcional)
   * @returns {Promise} - Promesa con los resultados
   */
  cambiarEtapa: async (historiaId, etapaId, motivo, notificacionId = null) => {
    return await api.post('/etapas/cambio-etapa', {
      historia_id: historiaId,
      etapa_id: etapaId,
      motivo,
      notificacion_id: notificacionId
    });
  },
  
 
  verificarProgresoEtapa: async (historiaEspecialidadId, etapaId) => {
    try {
      
      
      const response = await api.get(`/etapas/verificar-progreso/${historiaEspecialidadId}/${etapaId}`);
      
      // Manejar diferentes formatos de respuesta del backend
      const data = response.data || response;
      
      
      
      return { data };
    } catch (error) {
      console.error('❌ Error al verificar progreso de etapa:', error);
      
      // Log detallado del error
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        console.error('URL:', error.config?.url);
      }
      
      // Si es error 404, devolver estructura vacía pero válida
      if (error.response?.status === 404) {
        console.warn('⚠️ Etapa o historia no encontrada, devolviendo progreso vacío');
        return {
          data: {
            etapa: { id: etapaId, nombre: 'Etapa no encontrada' },
            progreso: {
              formularios: { total: 0, completados: 0, porcentaje: 0 },
              documentos: { total: 0, completados: 0, porcentaje: 0 },
              sesiones: { duracionEstimada: 0, realizadas: 0, porcentaje: 0 },
              general: 0
            },
            requisitosCompletados: false,
            puedeAvanzar: false
          }
        };
      }
      
      // Para otros errores, re-lanzar para que el componente pueda manejarlos
      throw error;
    }
  },
  
 
  asociarPlantilla: async (etapaId, plantillaId, requerido = true, orden = 1) => {
    return await api.post(`/etapas/${etapaId}/asociar-plantilla/${plantillaId}`, {
      requerido,
      orden
    });
  },
  
 
  desasociarPlantilla: async (etapaId, plantillaId) => {
    return await api.delete(`/etapas/${etapaId}/desasociar-plantilla/${plantillaId}`);
  },

 
  getAllEtapas: async () => {
    return await api.get('/etapas');
  },
  
 
  getEtapaById: async (id) => {
    return await api.get(`/etapas/${id}`);
  },
 
  createEtapa: async (data) => {
    return await api.post('/etapas', data);
  },
  
 
  updateEtapa: async (id, data) => {
    return await api.put(`/etapas/${id}`, data);
  },
 
  deleteEtapa: async (id) => {
    return await api.delete(`/etapas/${id}`);
  },
  
 
  getEtapasByHistoria: async (historiaId) => {
    // Primero obtenemos la historia para saber su especialidad y programa
    const resHistoria = await api.get(`/historias-clinicas/${historiaId}`);
    const { especialidad_id, programa_id } = resHistoria.data;
    
    // Ahora obtenemos las etapas que corresponden a esa especialidad y programa
    let url = `/etapas/especialidad/${especialidad_id}`;
    if (programa_id) url += `?programa_id=${programa_id}`;
    
    return await api.get(url);
  },
  
  getFormulariosPorEtapa: async (etapaId) => {
    try {
      const response = await api.get(`/formularios/etapa/${etapaId}`);
      return response.data || response; // Manejar diferentes formatos de respuesta
    } catch (error) {
      console.error('Error al obtener formularios por etapa:', error);
      throw error;
    }
  },

 
  reordenarEtapas: async (data) => {
    // Este método debería manejar una actualización en batch de órdenes de etapas
    // Como no tenemos un endpoint específico, actualizamos cada etapa individualmente
    const { etapas } = data;
    const promesas = etapas.map(etapa => 
      api.put(`/etapas/${etapa.id}`, { orden: etapa.orden })
    );
    
    return Promise.all(promesas);
  },

  // En etapaTratamientoService.js
    getEtapasDisponiblesPaciente: async (pacienteId, especialidadId) => {
      return await api.get(`/etapas/paciente/${pacienteId}/especialidad/${especialidadId}`);
    },

    cerrarEtapa: async (historiaEspecialidadId, etapaId, motivo = '') => {
  try {
    const response = await api.post(
      `/etapas/estados/${historiaEspecialidadId}/${etapaId}/cerrar`, 
      { motivo }
    );
    return response.data;
  } catch (error) {
    console.error('Error al cerrar etapa:', error);
    throw error;
  }
},

reabrirEtapa: async (historiaEspecialidadId, etapaId, motivo = '') => {
  try {
    const response = await api.post(
      `/etapas/estados/${historiaEspecialidadId}/${etapaId}/reabrir`, 
      { motivo }
    );
    return response.data;
  } catch (error) {
    console.error('Error al reabrir etapa:', error);
    throw error;
  }
},

getEstadoEtapa: async (historiaEspecialidadId, etapaId) => {
  try {
    const response = await api.get(
      `/etapas/estados/${historiaEspecialidadId}/${etapaId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener estado de etapa:', error);
    throw error;
  }
},
  
};

 
export const getEtapasByPrograma = async (programaId) => {
  try {
    const response = await api.get(`/etapas/programa/${programaId}`);
    return response.data; // El api service ya devuelve response.data
  } catch (error) {
    console.error('Error al obtener etapas por programa:', error);
    throw error;
  }
};

// Obtener etapas por especialidad - CORREGIDO
export const getEtapasByEspecialidad = async (especialidadId) => {
  try {
    const response = await api.get(`/etapas/especialidad/${especialidadId}`);
    return response.data; // El api service ya devuelve response.data
  } catch (error) {
    console.error('Error al obtener etapas por especialidad:', error);
    throw error;
  }
};


// Exportamos el objeto completo como default
export default etapaTratamientoService;

// Además, exportamos las funciones individuales para compatibilidad
export const {
  getAllEtapas,
  getEtapaById,
  createEtapa,
  updateEtapa,
  deleteEtapa,
  getEtapasByHistoria,
  reordenarEtapas
} = etapaTratamientoService;